import React from 'react'
import { Typography, IconButton, Drawer } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { AtlasIcon } from '@diligentcorp/atlas-react'
import { ColorBaseMulberryBlue, ColorBaseWhite } from 'assets/styles/variables'
import EnhancedPreviewTemplate from './enhanced-preview-template'
import { MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE, MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION, MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH } from 'common/constants/constants'
import i18n from 'i18next'
    
/**
 * IpadPreviewModal - Drawer version of IpadPreviewModal
 * This component provides a better user experience on iPad devices
 * by using a drawer instead of a modal, which provides better alignment
 * and navigation flow with the main template preview drawer
 */
const DRAWER_WIDTH = 658

const useStyles = makeStyles((theme) => ({
    drawer: {
        width: DRAWER_WIDTH,
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        },
        // iPad specific responsive behavior
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            width: '100vw'
        }
    },
    drawerPaper: {
        position: 'fixed',
        width: DRAWER_WIDTH,
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        },
        // iPad specific responsive behavior
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            width: '100vw'
        }
    },
    drawerContent: {
        width: '100%',
        height: '100vh',
        outline: 'none',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // iPad specific - use percentage instead of viewport height
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            height: '100%'
        }
    },
    drawerHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: theme.spacing(2, 3),
        backgroundColor: ColorBaseWhite,
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: 1,
        flexShrink: 0
    },
    drawerTitleContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    sidebarTitle: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        fontSize: '22px',
        lineHeight: '26px',
        letterSpacing: '0px'
    },
    sidebarDescription: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 400,
        fontSize: '16px',
        color: '#6B7280'
    },
    drawerBody: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f2f2f2',
        padding: theme.spacing(3),
        overflow: 'auto',
        minHeight: 0,
        '-webkit-overflow-scrolling': 'touch'
    },
    drawerCloseButton: {
        '&:focus, &:active': {
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    }
}))

interface IpadPreviewModalProps {
    open: boolean
    onClose: () => void
    systemTemplate?: any
    previewSelection?: any
    imageUrlsData?: string[]
    loading?: boolean
}

const IpadPreviewModal: React.FC<IpadPreviewModalProps> = ({
    open,
    onClose,
    systemTemplate,
    previewSelection,
    imageUrlsData = [],
    loading = false
}) => {
    const classes = useStyles()

    const truncateTitle = (title, limit = 366) =>
        title?.length > limit ? `${title.slice(0, limit)}...` : title

    const isSystemTemplateMatch = (tpl?: any) => {
        if (!tpl || !systemTemplate) return false
        if (Array.isArray(systemTemplate)) {
            return systemTemplate.some((t) => t?.id === tpl?.id)
        }
        return tpl?.id === systemTemplate?.id
    }

    const getPreviewTitle = () => {
        if (!systemTemplate) {
            return ''
        }
        if (isSystemTemplateMatch(systemTemplate)) {
            return systemTemplate?.title === MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE
                ? i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE')
                : i18n.t('MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE')
        }
        return previewSelection.title
    }

    const getPreviewDescription = () => {
        if (!systemTemplate) {
            return ''
        }
        if (isSystemTemplateMatch(systemTemplate)) {
            const fullText =
                systemTemplate?.description === MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION
                    ? i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION')
                    : i18n.t('MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION')
            return truncateTitle(fullText, MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH)
        }
        return truncateTitle(previewSelection.description, MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH)
    }

    return (
        <Drawer
            className={classes.drawer}
            classes={{ paper: classes.drawerPaper }}
            anchor="right"
            open={open}
            onClose={onClose}
        >
            <div className={classes.drawerContent}>
                {/* Drawer Header */}
                <div className={classes.drawerHeader}>
                    <div className={classes.drawerTitleContainer}>
                        <Typography className={classes.sidebarTitle}>
                            {getPreviewTitle()}
                        </Typography>
                        <Typography className={classes.sidebarDescription}>
                            {getPreviewDescription()}
                        </Typography>
                    </div>
                    <IconButton
                        onClick={onClose}
                        className={classes.drawerCloseButton}
                        size="small"
                        aria-label="Close Preview"
                    >
                        <AtlasIcon name="close" />
                    </IconButton>
                </div>

                {/* Drawer Body */}
                <div className={classes.drawerBody}>
                    <EnhancedPreviewTemplate imageUrlsData={imageUrlsData} loading={loading} />
                </div>
            </div>
        </Drawer>
    )
}

export default IpadPreviewModal
